import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 11, fontFamily: 'Helvetica', backgroundColor: '#ffffff' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 30 },
  logo: { width: 100, height: 100, objectFit: 'contain' },
  companyInfo: { textAlign: 'right', maxWidth: '40%' },
  companyName: { fontSize: 14, fontWeight: 'bold', marginBottom: 3 },
  companyDetails: { fontSize: 9, color: '#666', marginBottom: 2 },
  poTitle: { fontSize: 28, fontWeight: 'bold', color: '#667eea', marginBottom: 5 },
  poNumber: { fontSize: 12, color: '#666', marginBottom: 20 },
  section: { marginBottom: 20 },
  sectionTitle: { fontSize: 10, fontWeight: 'bold', color: '#667eea', marginBottom: 8, textTransform: 'uppercase' },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 15 },
  infoBlock: { width: '48%' },
  label: { fontSize: 9, color: '#888', marginBottom: 3 },
  value: { fontSize: 11, marginBottom: 2 },
  table: { marginTop: 20, marginBottom: 20 },
  tableHeader: { flexDirection: 'row', backgroundColor: '#667eea', color: '#ffffff', padding: 8, fontWeight: 'bold', fontSize: 9 },
  tableRow: { flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 8, borderBottomWidth: 1, borderColor: '#f0f0f0', fontSize: 9 },
  colItem: { width: '30%' },
  colSku: { width: '10%', textAlign: 'center' },
  colOrdered: { width: '10%', textAlign: 'center' },
  colReceived: { width: '10%', textAlign: 'center' },
  colCost: { width: '15%', textAlign: 'right' },
  colTotal: { width: '15%', textAlign: 'right', fontWeight: 'bold' },
  totalsSection: { marginTop: 20, alignItems: 'flex-end' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', width: '50%', paddingVertical: 5, fontSize: 11 },
  subtotalRow: { borderBottomWidth: 1, borderColor: '#e0e0e0', marginBottom: 5 },
  grandTotalRow: { backgroundColor: '#667eea', color: '#ffffff', padding: 10, marginTop: 5, fontSize: 14, fontWeight: 'bold' },
  footer: { position: 'absolute' as const, bottom: 30, left: 40, right: 40, textAlign: 'center' as const, fontSize: 9, color: '#999', borderTopWidth: 1, borderColor: '#e0e0e0', paddingTop: 10 }
});

const POPdf: React.FC<{ po: any; businessSettings?: any }> = ({ po, businessSettings }) => {
  const { API_URL } = require('../config/api');
  const logoUrl = businessSettings?.logo?.startsWith('http') 
    ? businessSettings.logo 
    : businessSettings?.logo 
      ? `${API_URL.replace('/api','')}${businessSettings.logo}` 
      : null;

  const subtotal = (po.items || []).reduce((sum: number, item: any) => {
    return sum + (parseFloat(item.quantity_ordered || 0) * parseFloat(item.unit_cost || 0));
  }, 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          <View>
            {logoUrl && <Image src={logoUrl} style={styles.logo} />}
          </View>
          <View style={styles.companyInfo}>
            {businessSettings?.name && <Text style={styles.companyName}>{businessSettings.name}</Text>}
            {businessSettings?.email && <Text style={styles.companyDetails}>{businessSettings.email}</Text>}
            {businessSettings?.phone && <Text style={styles.companyDetails}>Tel: {businessSettings.phone}</Text>}
            {businessSettings?.website && <Text style={styles.companyDetails}>{businessSettings.website}</Text>}
            {businessSettings?.companyNumber && <Text style={styles.companyDetails}>Company: {businessSettings.companyNumber}</Text>}
            {businessSettings?.vatNumber && <Text style={styles.companyDetails}>VAT: {businessSettings.vatNumber}</Text>}
          </View>
        </View>

        <Text style={styles.poTitle}>PURCHASE ORDER</Text>
        <Text style={styles.poNumber}>PO #{po.po_number}</Text>

        <View style={styles.infoRow}>
          <View style={styles.infoBlock}>
            <Text style={styles.label}>SUPPLIER</Text>
            <Text style={styles.value}>{po.supplier_name || 'N/A'}</Text>
            <Text style={styles.label}>ORDER DATE</Text>
            <Text style={styles.value}>
              {po.created_at
                ? new Date(po.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              }
            </Text>
            <Text style={styles.label}>STATUS</Text>
            <Text style={styles.value}>{po.status || 'Draft'}</Text>
          </View>
          <View style={styles.infoBlock}>
            <Text style={styles.label}>EXPECTED DELIVERY</Text>
            <Text style={styles.value}>
              {po.expected_date
                ? new Date(po.expected_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                : 'N/A'
              }
            </Text>
            <Text style={styles.label}>DELIVERY ADDRESS</Text>
            <Text style={[styles.value, { fontSize: 9 }]}>
              {[businessSettings?.address, businessSettings?.city, businessSettings?.postcode, businessSettings?.country]
                .filter(Boolean)
                .join(', ') || 'N/A'}
            </Text>
          </View>
        </View>

        {po.items && po.items.length > 0 && (
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={styles.colItem}>Product</Text>
              <Text style={styles.colSku}>SKU</Text>
              <Text style={styles.colOrdered}>Ordered</Text>
              <Text style={styles.colReceived}>Received</Text>
              <Text style={styles.colCost}>Unit Cost</Text>
              <Text style={styles.colTotal}>Total</Text>
            </View>
            {po.items.map((item: any, index: number) => {
              const qty = parseFloat(item.quantity_ordered) || 0;
              const received = parseFloat(item.quantity_received || 0);
              const cost = parseFloat(item.unit_cost) || 0;
              const lineTotal = qty * cost;
              return (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.colItem}>{item.product_name || 'Item'}</Text>
                  <Text style={styles.colSku}>{item.sku || '—'}</Text>
                  <Text style={styles.colOrdered}>{qty}</Text>
                  <Text style={styles.colReceived}>{received}</Text>
                  <Text style={styles.colCost}>£{cost.toFixed(2)}</Text>
                  <Text style={styles.colTotal}>£{lineTotal.toFixed(2)}</Text>
                </View>
              );
            })}
          </View>
        )}

        <View style={styles.totalsSection}>
          <View style={[styles.totalRow, styles.subtotalRow]}>
            <Text>Subtotal:</Text>
            <Text>£{subtotal.toFixed(2)}</Text>
          </View>
          <View style={[styles.totalRow, styles.grandTotalRow]}>
            <Text>TOTAL:</Text>
            <Text>£{(parseFloat(po.total) || subtotal).toFixed(2)}</Text>
          </View>
        </View>

        {po.notes && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Notes</Text>
            <Text style={styles.value}>{po.notes}</Text>
          </View>
        )}

        <View style={styles.footer}>
          <Text>Please confirm receipt of this order</Text>
          {businessSettings?.email && <Text>{businessSettings.email}</Text>}
          {businessSettings?.phone && <Text>{businessSettings.phone}</Text>}
        </View>
      </Page>
    </Document>
  );
};

export default POPdf;
